import React, { useEffect, useRef, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import * as THREE from 'three';
import Header from './Header';
import Seo from './Seo';
import Footer from './Footer';
import FaqSchema from './FaqSchema';
import ProductSidebar from './ProductSidebar';
import { useUser } from '../contexts/UserContext';
import './ATSCheckLanding.css';

const HERO_IMG = '/images/ats-check/hero-ats-scanner.png';
const BOTS_IMG = '/images/ats-check/beat-the-bots.png';
const KEYWORD_IMG = '/images/ats-check/keyword-density.png';
const RADAR_IMG = '/images/ats-check/optimization-radar.png';

/* ---------- Three.js scan-line canvas ---------- */
const ScanGateCanvas = () => {
  const mountRef = useRef(null);
  useEffect(() => {
    const mount = mountRef.current;
    if (!mount) return;
    let width = mount.clientWidth;
    let height = mount.clientHeight;

    const scene = new THREE.Scene();
    const camera = new THREE.PerspectiveCamera(60, width / height, 0.1, 1000);
    camera.position.z = 70;

    const renderer = new THREE.WebGLRenderer({ alpha: true, antialias: true });
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    renderer.setSize(width, height);
    renderer.setClearColor(0x000000, 0);
    mount.appendChild(renderer.domElement);

    const sprite = (() => {
      const c = document.createElement('canvas');
      c.width = c.height = 64;
      const ctx = c.getContext('2d');
      const g = ctx.createRadialGradient(32, 32, 0, 32, 32, 32);
      g.addColorStop(0, 'rgba(255,255,255,1)');
      g.addColorStop(0.35, 'rgba(224,163,255,0.95)');
      g.addColorStop(0.7, 'rgba(147,112,219,0.4)');
      g.addColorStop(1, 'rgba(0,0,0,0)');
      ctx.fillStyle = g;
      ctx.fillRect(0, 0, 64, 64);
      return new THREE.CanvasTexture(c);
    })();

    // ambient particle field
    const fieldCount = 220;
    const fieldPos = new Float32Array(fieldCount * 3);
    const fieldSpeeds = new Float32Array(fieldCount);
    for (let i = 0; i < fieldCount; i++) {
      fieldPos[i * 3] = (Math.random() - 0.5) * 160;
      fieldPos[i * 3 + 1] = (Math.random() - 0.5) * 100;
      fieldPos[i * 3 + 2] = (Math.random() - 0.5) * 50;
      fieldSpeeds[i] = 0.04 + Math.random() * 0.10;
    }
    const fieldGeo = new THREE.BufferGeometry();
    fieldGeo.setAttribute('position', new THREE.BufferAttribute(fieldPos, 3));
    const fieldMat = new THREE.PointsMaterial({
      size: 1.0, map: sprite, transparent: true,
      depthWrite: false, blending: THREE.AdditiveBlending,
      color: 0xffffff, opacity: 0.4,
    });
    const field = new THREE.Points(fieldGeo, fieldMat);
    scene.add(field);

    let raf;
    let mx = 0, my = 0, t = 0;
    const onMove = (e) => {
      const r = mount.getBoundingClientRect();
      mx = ((e.clientX - r.left) / r.width - 0.5) * 2;
      my = ((e.clientY - r.top) / r.height - 0.5) * 2;
    };
    window.addEventListener('mousemove', onMove);
    const onResize = () => {
      width = mount.clientWidth; height = mount.clientHeight;
      camera.aspect = width / height;
      camera.updateProjectionMatrix();
      renderer.setSize(width, height);
    };
    window.addEventListener('resize', onResize);

    const animate = () => {
      t += 0.012;
      const fp = fieldGeo.attributes.position.array;
      for (let i = 0; i < fieldCount; i++) {
        fp[i * 3 + 1] += fieldSpeeds[i] * 0.5;
        if (fp[i * 3 + 1] > 50) fp[i * 3 + 1] = -50;
      }
      fieldGeo.attributes.position.needsUpdate = true;

      camera.position.x += (mx * 6 - camera.position.x) * 0.04;
      camera.position.y += (-my * 4 - camera.position.y) * 0.04;
      camera.lookAt(0, 0, 0);

      renderer.render(scene, camera);
      raf = requestAnimationFrame(animate);
    };
    animate();

    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('resize', onResize);
      fieldGeo.dispose(); fieldMat.dispose();
      sprite.dispose();
      renderer.dispose();
      if (renderer.domElement.parentNode === mount) mount.removeChild(renderer.domElement);
    };
  }, []);

  return <div ref={mountRef} className="acl-hero-canvas" aria-hidden="true" />;
};

/* ---------- Score gauge demo (counts 0 → 87 on scroll) ---------- */
const useCounter = (target, start) => {
  const [val, setVal] = useState(0);
  useEffect(() => {
    if (!start) return;
    const isFloat = !Number.isInteger(target);
    const dur = 1600;
    const t0 = performance.now();
    let raf;
    const step = (t) => {
      const p = Math.min(1, (t - t0) / dur);
      const eased = 1 - Math.pow(1 - p, 3);
      const v = target * eased;
      setVal(isFloat ? Math.round(v * 10) / 10 : Math.round(v));
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [target, start]);
  return val;
};

const ScoreGaugeDemo = () => {
  const ref = useRef(null);
  const [start, setStart] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting) setStart(true); }),
      { threshold: 0.4 },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  const score = useCounter(87, start);
  const circumference = 2 * Math.PI * 70;
  const offset = circumference * (1 - score / 100);

  return (
    <section className="acl-section" ref={ref}>
      <div className="acl-section-grid">
        <div className="acl-section-text">
          <span className="acl-eyebrow">SINGLE-NUMBER ANSWER</span>
          <h2 className="acl-h2">
            One score that tells you <span className="acl-grad-text">if it'll get through</span>.
          </h2>
          <p className="acl-lead">
            We don't dump 14 metrics on you. The ATS Match score answers the only question that
            matters: will this resume reach a human reviewer? Above 80 is the bar. Below — we tell
            you exactly what's holding it back.
          </p>
          <ul className="acl-bullet-list">
            <li><span className="acl-dot" />Format compliance (parseable layout)</li>
            <li><span className="acl-dot" />Keyword & skill density vs. JD</li>
            <li><span className="acl-dot" />Section ordering & headings</li>
            <li><span className="acl-dot" />Length & readability heuristics</li>
          </ul>
        </div>

        <div className="acl-gauge-card">
          <svg className="acl-gauge-svg" viewBox="0 0 160 160">
            <defs>
              <linearGradient id="aclGaugeGrad" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#00ffff" />
                <stop offset="50%" stopColor="#6fffd1" />
                <stop offset="100%" stopColor="#ffd166" />
              </linearGradient>
            </defs>
            <circle cx="80" cy="80" r="70" stroke="rgba(255,255,255,0.08)" strokeWidth="10" fill="none" />
            <circle
              cx="80" cy="80" r="70"
              stroke="url(#aclGaugeGrad)"
              strokeWidth="10" fill="none"
              strokeLinecap="round"
              strokeDasharray={circumference}
              strokeDashoffset={offset}
              transform="rotate(-90 80 80)"
              style={{ transition: 'stroke-dashoffset 0.4s linear', filter: 'drop-shadow(0 0 8px rgba(0,255,255,0.5))' }}
            />
          </svg>
          <div className="acl-gauge-label">
            <div className="acl-gauge-num">{score}</div>
            <div className="acl-gauge-sub">/ 100 ATS MATCH</div>
          </div>
          <div className="acl-gauge-band">
            <span className={score >= 80 ? 'pass' : 'fail'}>{score >= 80 ? '✓ Passes ATS filters' : '⚠ Below threshold'}</span>
            <span>vs. job description</span>
          </div>
        </div>
      </div>
    </section>
  );
};

/* ---------- Keyword match demo (animated chips) ---------- */
const KEYWORD_BANK = [
  { word: 'React', match: true },
  { word: 'TypeScript', match: true },
  { word: 'CI/CD', match: true },
  { word: 'AWS', match: true },
  { word: 'Docker', match: true },
  { word: 'GraphQL', match: false },
  { word: 'PostgreSQL', match: true },
  { word: 'Kubernetes', match: false },
  { word: 'Microservices', match: true },
  { word: 'Terraform', match: false },
];

const KeywordDemo = () => {
  const ref = useRef(null);
  const [revealed, setRevealed] = useState(0);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting && revealed === 0) setRevealed(1); }),
      { threshold: 0.4 },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [revealed]);
  useEffect(() => {
    if (revealed === 0 || revealed >= KEYWORD_BANK.length) return;
    const t = setTimeout(() => setRevealed((r) => r + 1), 220);
    return () => clearTimeout(t);
  }, [revealed]);

  const matchCount = KEYWORD_BANK.slice(0, revealed).filter((k) => k.match).length;
  const missCount = revealed - matchCount;

  return (
    <section className="acl-section" ref={ref}>
      <div className="acl-section-grid acl-flip">
        <div className="acl-keyword-card">
          <div className="acl-keyword-head">
            <span>JD KEYWORDS DETECTED · {KEYWORD_BANK.length}</span>
            <div className="acl-keyword-stats">
              <span className="acl-stat-pill match">✓ {matchCount}</span>
              <span className="acl-stat-pill miss">✗ {missCount}</span>
            </div>
          </div>
          <div className="acl-keyword-cloud">
            {KEYWORD_BANK.map((k, i) => (
              <span
                key={k.word}
                className={`acl-keyword-chip ${i < revealed ? 'lit' : ''} ${k.match ? 'match' : 'miss'}`}
                style={{ transitionDelay: `${i * 30}ms` }}
              >
                {k.word}
              </span>
            ))}
          </div>
          <div className="acl-keyword-action">
            <span>Add the {missCount} missing keywords →</span>
          </div>
        </div>

        <div className="acl-section-text">
          <span className="acl-eyebrow">KEYWORD COVERAGE</span>
          <h2 className="acl-h2">
            See exactly which keywords <span className="acl-grad-text">you're missing</span>.
          </h2>
          <p className="acl-lead">
            We extract every keyword the JD treats as important, then map each one against your
            resume. Green chips are matched. Red chips are gaps you should address — naturally,
            not by stuffing.
          </p>
          <ul className="acl-bullet-list">
            <li><span className="acl-dot" />Hard skills, tools, frameworks, methodologies</li>
            <li><span className="acl-dot" />Soft skills + behavioral signals from the JD</li>
            <li><span className="acl-dot" />Both acronyms (CI/CD) and full forms detected</li>
          </ul>
        </div>
      </div>
    </section>
  );
};

/* ---------- Process flow ---------- */
const FLOW_STEPS = [
  { title: 'Drop the JD', body: 'Paste the job description or upload it as PDF / DOCX.' },
  { title: 'Add your resume', body: 'PDF / DOCX upload, or paste the text directly.' },
  { title: 'Analyze', body: 'Sub-second scan — format, keywords, sections, length, readability.' },
  { title: 'Read the report', body: 'Single ATS Match score + per-axis breakdown of what to fix.' },
  { title: 'Tailor in one click', body: 'Push your resume through our AI to address every gap automatically.' },
  { title: 'Re-scan & ship', body: 'Run the check again. When you\'re above 85, you\'re ready to apply.' },
];

const FlowSection = () => {
  const ref = useRef(null);
  const [active, setActive] = useState(-1);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting) setActive(0); }),
      { threshold: 0.25 },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  useEffect(() => {
    if (active < 0 || active >= FLOW_STEPS.length - 1) return;
    const t = setTimeout(() => setActive((a) => a + 1), 700);
    return () => clearTimeout(t);
  }, [active]);

  return (
    <section className="acl-section" ref={ref}>
      <div className="acl-section-head">
        <span className="acl-eyebrow">HOW IT WORKS</span>
        <h2 className="acl-h2">
          Six steps. <span className="acl-grad-text">From "ghosted" to "shortlisted".</span>
        </h2>
        <p className="acl-lead acl-center">
          Most resumes never reach a human. Use this so yours does.
        </p>
      </div>
      <div className="acl-flow-grid">
        {FLOW_STEPS.map((s, i) => (
          <div key={s.title} className={`acl-flow-node ${i <= active ? 'lit' : ''}`}>
            <div className="acl-flow-num">{String(i + 1).padStart(2, '0')}</div>
            <h3>{s.title}</h3>
            <p>{s.body}</p>
            {i < FLOW_STEPS.length - 1 && <div className={`acl-flow-link ${i < active ? 'lit' : ''}`} />}
          </div>
        ))}
      </div>
    </section>
  );
};

/* ---------- Stats ---------- */
const STATS = [
  { val: 90, suffix: '%', label: 'of large employers use ATS' },
  { val: 75, suffix: '%', label: 'of resumes get rejected by ATS' },
  { val: 1, suffix: 's', label: 'avg analysis time' },
  { val: 200, suffix: 'K+', label: 'Resumes scanned' },
];
const StatTile = ({ stat, start }) => {
  const v = useCounter(stat.val, start);
  return (
    <div className="acl-stat">
      <div className="acl-stat-num">{v}{stat.suffix}</div>
      <div className="acl-stat-lbl">{stat.label}</div>
    </div>
  );
};
const StatsBand = () => {
  const ref = useRef(null);
  const [start, setStart] = useState(false);
  useEffect(() => {
    const el = ref.current; if (!el) return;
    const obs = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting) setStart(true); }),
      { threshold: 0.3 },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return (
    <section className="acl-stats-band" ref={ref}>
      <div className="acl-stats-glow" />
      {STATS.map((s) => <StatTile key={s.label} stat={s} start={start} />)}
    </section>
  );
};

/* ---------- FAQ ---------- */
const FAQS = [
  { q: 'What does the score mean?', a: 'It\'s a 0–100 number representing how likely your resume is to pass an ATS filter for the JD you provided. 80+ is the typical pass bar; below that we surface the specific gaps to address.' },
  { q: 'Do real ATS systems work like this?', a: 'They\'re close cousins. Real ATS engines (Workday, Greenhouse, Taleo, iCIMS, etc.) all weight format compliance, keyword match, and section parseability. We replicate that scoring against the most common patterns.' },
  { q: 'Can I tailor my resume right after scoring?', a: 'Yes — one click and our AI tailors your resume to address every gap surfaced in the report. You can then re-scan to confirm the score went up.' },
  { q: 'Is keyword stuffing dangerous?', a: 'Yes. ATS engines flag obvious stuffing, and human reviewers immediately distrust it. Our recommendations are about natural integration, never repetition.' },
  { q: 'Does formatting matter that much?', a: 'Massively. Tables, multi-column layouts, headers/footers, embedded images, and exotic fonts all break ATS parsing. We detect each one specifically.' },
  { q: 'How fresh is the analysis?', a: 'Every scan is fresh. Nothing is cached — paste the latest JD and your latest resume and you get a current report.' },
];

const FaqAccordion = () => {
  const [open, setOpen] = useState(0);
  return (
    <section className="acl-section">
      <div className="acl-section-head">
        <span className="acl-eyebrow">QUESTIONS, ANSWERED</span>
        <h2 className="acl-h2">Everything you wanted to ask</h2>
      </div>
      <div className="acl-faq-list">
        {FAQS.map((f, i) => (
          <button key={f.q} type="button" className={`acl-faq-item ${open === i ? 'open' : ''}`} onClick={() => setOpen(open === i ? -1 : i)}>
            <div className="acl-faq-q">
              <span>{f.q}</span>
              <span className="acl-faq-chev">+</span>
            </div>
            <div className="acl-faq-a"><p>{f.a}</p></div>
          </button>
        ))}
      </div>
    </section>
  );
};

/* ---------- Main page ---------- */
const ATSCheckLanding = () => {
  const navigate = useNavigate();
  // The scanner is a full workspace now — open it on its own route rather
  // than scrolling to an embedded copy of the tool.
  const goStudio = useCallback(() => navigate('/ats-studio'), [navigate]);
  const { user } = useUser();
  const setShowAuthModal = useCallback((show) => { if (show) navigate('/?openLogin=1'); }, [navigate]);

  const heroRef = useRef(null);
  useEffect(() => {
    const el = heroRef.current;
    if (!el) return;
    const onMove = (e) => {
      const r = el.getBoundingClientRect();
      const x = (e.clientX - r.left) / r.width - 0.5;
      const y = (e.clientY - r.top) / r.height - 0.5;
      el.style.setProperty('--mx', x);
      el.style.setProperty('--my', y);
    };
    el.addEventListener('mousemove', onMove);
    return () => el.removeEventListener('mousemove', onMove);
  }, []);

  return (
    <div className="acl-page">
      <Seo
        title="Free ATS Resume Checker & Score"
        description="Scan your resume against applicant tracking systems, get an ATS score, and fix issues before you apply."
        path="/ATSCheck"
      />
      <Header setShowAuthModal={setShowAuthModal} />
      <ProductSidebar user={user} setShowAuthModal={setShowAuthModal} />

      <section className="acl-hero" ref={heroRef}>
        <ScanGateCanvas />
        <div className="acl-hero-bg" />
        <div className="acl-hero-halo" aria-hidden="true" />
        <img src={HERO_IMG} alt="" className="acl-hero-img" loading="eager" />

        <div className="acl-hero-content">
          <span className="acl-hero-pill">
            <span className="acl-pill-dot" /> Sub-second analysis · Beats real ATS engines
          </span>
          <h1 className="acl-h1">
            Find out if your resume <span className="acl-grad-text">survives the bots</span>.
          </h1>
          <p className="acl-hero-sub">
            75% of resumes are filtered out by ATS before a human ever reads them. We scan yours
            the same way they do — single number, clear gaps, one-click fix.
          </p>

          <div className="acl-hero-cta">
            <button className="acl-btn acl-btn-primary" onClick={goStudio}>
              <span>Run my ATS scan</span>
              <svg width="18" height="18" viewBox="0 0 24 24"><path fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" d="M5 12h14M13 5l7 7-7 7"/></svg>
            </button>
            <button className="acl-btn acl-btn-ghost" onClick={() => navigate('/resume-builder')}>
              Build resume first
            </button>
          </div>

          <div className="acl-hero-checks">
            <span>✓ Single ATS Match score</span>
            <span>✓ Per-keyword breakdown</span>
            <span>✓ One-click tailor</span>
          </div>
        </div>

        {/* Floating glass score card */}
        <div className="acl-floater" aria-hidden="true">
          <div className="acl-floater-card">
            <div className="acl-floater-head">
              <span className="acl-floater-dot" />
              <span>SCAN COMPLETE</span>
            </div>
            <div className="acl-floater-num">87<span>/100</span></div>
            <div className="acl-floater-bar">
              <div className="acl-floater-bar-fill" />
            </div>
            <div className="acl-floater-meta">
              <span>Format ✓ 9.2</span>
              <span>Keywords ✓ 8.4</span>
              <span>Length ⚠ 6.1</span>
            </div>
          </div>
          <div className="acl-floater-glow" />
        </div>
      </section>

      <section className="acl-studio" id="studio">
        <div className="acl-studio-head">
          <span className="acl-eyebrow">ATS CHECK STUDIO</span>
          <h2 className="acl-h2">
            A workspace built for <span className="acl-grad-text">one job at a time</span>.
          </h2>
          <p className="acl-lead acl-center">
            Job description and resume on the left, your live report on the right. Edit, re-scan,
            repeat — until the score reads the way you want it to.
          </p>
        </div>

        <button type="button" className="acl-studio-card" onClick={goStudio}>
          <div className="acl-studio-mock" aria-hidden="true">
            <div className="acl-mock-pane">
              <span className="acl-mock-label">Job Description</span>
              <i /><i /><i />
              <span className="acl-mock-label">Your Resume</span>
              <i /><i /><i />
            </div>
            <div className="acl-mock-pane acl-mock-pane--report">
              <span className="acl-mock-label">ATS Report</span>
              <div className="acl-mock-score">87<span>/100</span></div>
              <div className="acl-mock-chips"><em /><em /><em /><em /></div>
              <i /><i /><i />
            </div>
          </div>
          <div className="acl-studio-cta">
            <span className="acl-btn acl-btn-primary">
              Open ATS Studio
              <svg width="18" height="18" viewBox="0 0 24 24"><path fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" d="M5 12h14M13 5l7 7-7 7"/></svg>
            </span>
            <span className="acl-studio-note">
              Full-screen scanner — score, missing keywords, and format fixes in about a second.
            </span>
          </div>
        </button>
      </section>

      <ScoreGaugeDemo />
      <KeywordDemo />

      <section className="acl-section">
        <div className="acl-section-grid">
          <div className="acl-section-text">
            <span className="acl-eyebrow">BEAT THE BOTS</span>
            <h2 className="acl-h2">
              90% of large companies use ATS. <span className="acl-grad-text">Yours has to make it through.</span>
            </h2>
            <p className="acl-lead">
              Workday, Greenhouse, Taleo, iCIMS — every Fortune 500 runs your resume through a
              filter before any human sees it. The filters look for parseable format, keyword
              density, and section structure. Miss any one and you get auto-rejected silently.
            </p>
            <ul className="acl-bullet-list">
              <li><span className="acl-dot" />75% of resumes never make it to a human</li>
              <li><span className="acl-dot" />Most rejections aren't about content — they're about format</li>
              <li><span className="acl-dot" />Optimization is a one-hour exercise, not a rewrite</li>
            </ul>
          </div>
          <div className="acl-img-frame">
            <img src={BOTS_IMG} alt="Beat the bots" />
            <div className="acl-img-frame-glow" />
          </div>
        </div>
      </section>

      <FlowSection />
      <StatsBand />

      <section className="acl-section">
        <div className="acl-section-grid acl-flip">
          <div className="acl-img-frame">
            <img src={KEYWORD_IMG} alt="Keyword density" />
            <div className="acl-img-frame-glow" />
          </div>
          <div className="acl-section-text">
            <span className="acl-eyebrow">KEYWORD DENSITY</span>
            <h2 className="acl-h2">
              Optimization is <span className="acl-grad-text">measurable</span>, not vibes.
            </h2>
            <p className="acl-lead">
              We measure keyword density per role-relevant term. Too low and the ATS skips you.
              Too high and you'll be flagged for stuffing. We hit the sweet spot — natural,
              comprehensive, recruiter-readable.
            </p>
          </div>
        </div>
      </section>

      <section className="acl-section">
        <div className="acl-section-grid">
          <div className="acl-section-text">
            <span className="acl-eyebrow">5-AXIS DIAGNOSIS</span>
            <h2 className="acl-h2">
              Five axes of <span className="acl-grad-text">specific, actionable</span> feedback.
            </h2>
            <p className="acl-lead">
              Format, Keywords, Sections, Length, Readability. Each scored, each with a line of
              specific advice — not "improve your resume" but "your Education section is below
              your Skills section; flip them."
            </p>
          </div>
          <div className="acl-img-frame">
            <img src={RADAR_IMG} alt="Optimization radar" />
            <div className="acl-img-frame-glow" />
          </div>
        </div>
      </section>

      <FaqAccordion />

      <section className="acl-cta-final">
        <div className="acl-cta-card">
          <div className="acl-cta-glow" />
          <h2 className="acl-h2">
            Don't let bots <span className="acl-grad-text">silently delete your resume</span>.
          </h2>
          <p>One scan. One score. One click to fix what's broken.</p>
          <div className="acl-hero-cta center">
            <button className="acl-btn acl-btn-primary" onClick={goStudio}>
              <span>Run my ATS scan</span>
              <svg width="18" height="18" viewBox="0 0 24 24"><path fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" d="M5 12h14M13 5l7 7-7 7"/></svg>
            </button>
            <button className="acl-btn acl-btn-ghost" onClick={() => navigate('/resume-builder')}>Build resume first</button>
          </div>
        </div>
      </section>

      <FaqSchema faqs={FAQS} />
      <Footer />
    </div>
  );
};

export default ATSCheckLanding;
